import { computed, type ComputedRef } from 'vue';
import { Actions, Subjects } from '@boqnet/permissions';
import { useCan } from '@/composables/use-can';
import { useFeatureFlagsStore } from '@/stores/feature-flags.store';
import { usePermissionStore } from '@/stores/permission.store';

export type PaymentsSurfaceAccess = 'pending' | 'allowed' | 'refused';

export interface PaymentsSurfaceAccessState {
  /** Whether the answer below is final: LaunchDarkly and the boot-time role refresh have settled. */
  isResolved: ComputedRef<boolean>;
  /** The flag is on and the user holds the payments-surface ability. Shows the tab. */
  canSeePaymentsSurface: ComputedRef<boolean>;
  /** Resolved lifecycle access to every payment write, using the unchanged CASL rule. */
  canActOnPayments: ComputedRef<boolean>;
  /** What the routes do: hold while pending, then render or leave. */
  access: ComputedRef<PaymentsSurfaceAccess>;
}

/**
 * Who may reach the payments surface: `erp_app_proof_of_payment_ingestion_v1` on and
 * `LIST` on the payments-surface subject, the six DL-119 roles (DL-152).
 *
 * One composable for the strip's tab, the routes' guard and the module, so the tab and the URL
 * can never disagree. The guard calls this as well as relying on route meta, because meta cannot
 * read the flag. Payment actions share canActOnPayments so entry from outside that guarded
 * surface still waits for resolved permissions (FR-P16).
 */
export const usePaymentsSurfaceAccess = (): PaymentsSurfaceAccessState => {
  const { can } = useCan();
  const permissionStore = usePermissionStore();
  const featureFlagsStore = useFeatureFlagsStore();

  // Context: a cold load (a reload, a pasted link, PE-741's Awaiting proof link) runs the guard
  // before LaunchDarkly and the role refresh settle. Refusing then would drop the link for good,
  // so nothing is refused until all three markers hold (DL-167 D17, the PE-1081 precedent).
  const isResolved = computed(
    () =>
      permissionStore.isInitialized &&
      permissionStore.areRoleFlagsResolved &&
      permissionStore.areRolesRefreshed,
  );

  const canSeePaymentsSurface = computed(
    () => featureFlagsStore.paymentLifecycleEnabled && can(Actions.LIST, Subjects.PAYMENTS_SURFACE),
  );

  const canActOnPayments = computed(
    () =>
      isResolved.value &&
      featureFlagsStore.paymentLifecycleEnabled &&
      can(Actions.CREATE, Subjects.PAYMENT),
  );

  const access = computed<PaymentsSurfaceAccess>(() => {
    if (!isResolved.value) return 'pending';
    return canSeePaymentsSurface.value ? 'allowed' : 'refused';
  });

  return { isResolved, canSeePaymentsSurface, canActOnPayments, access };
};
